/**
 * Firebase Firestore utility for storing layout configurations
 * Layouts can be public (visible to all, admin-only) or private (visible to author only)
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { loadLayoutState, saveLayoutState } from './layoutStorage';

const COLLECTION_NAME = 'layout_configurations';
const LAYOUT_TYPE = 'ride_view';

/**
 * Save a layout configuration to Firestore
 * @param {Object} params
 * @param {string} params.name - Layout name
 * @param {boolean} params.isPublic - Whether layout is public (admin-only)
 * @param {boolean} params.isDefault - Whether to set as default layout
 * @param {string} params.authorId - User ID of the author
 * @param {Object} params.layoutData - Layout data to save
 * @param {string} [params.overwriteLayoutId] - ID of layout to overwrite
 * @returns {Promise<Object>} Created or updated layout configuration
 */
export async function saveLayoutConfig({
  name,
  isPublic,
  isDefault = false,
  authorId,
  layoutData,
  overwriteLayoutId = null,
}) {
  try {
    if (overwriteLayoutId) {
      const docRef = doc(db, COLLECTION_NAME, overwriteLayoutId);
      const updateData = {
        name: name.trim(),
        is_public: isPublic,
        layout_data: layoutData,
        updated_at: serverTimestamp(),
      };
      if (isDefault !== undefined) {
        updateData.is_default = isDefault;
      }
      await updateDoc(docRef, updateData);

      if (isDefault) {
        await unmarkOtherDefaults(overwriteLayoutId, authorId);
      }

      const docSnap = await getDoc(docRef);
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name,
        is_public: data.is_public,
        is_default: data.is_default || false,
        author_id: data.author_id,
        layout_data: data.layout_data,
        created_at: data.created_at?.toDate ? data.created_at.toDate() : data.created_at,
        updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : data.updated_at,
      };
    }

    const collectionRef = collection(db, COLLECTION_NAME);
    const docData = {
      name: name.trim(),
      is_public: isPublic,
      is_default: isDefault || false,
      author_id: authorId,
      layout_data: layoutData,
      layout_type: LAYOUT_TYPE,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    };

    const docRef = await addDoc(collectionRef, docData);

    if (isDefault) {
      await unmarkOtherDefaults(docRef.id, authorId);
    }

    return {
      id: docRef.id,
      name: name.trim(),
      is_public: isPublic,
      is_default: isDefault || false,
      author_id: authorId,
      layout_data: layoutData,
      created_at: new Date(),
      updated_at: new Date(),
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to save layout configuration:', error);
    }
    throw error;
  }
}

async function unmarkOtherDefaults(currentLayoutId, authorId) {
  try {
    if (!authorId) return;
    const q = query(
      collection(db, COLLECTION_NAME),
      where('layout_type', '==', LAYOUT_TYPE),
      where('author_id', '==', authorId),
      where('is_default', '==', true)
    );
    const querySnapshot = await getDocs(q);
    const updates = [];
    querySnapshot.docs.forEach((docSnap) => {
      if (docSnap.id !== currentLayoutId) {
        updates.push(updateDoc(docSnap.ref, { is_default: false }));
      }
    });
    if (updates.length > 0) {
      await Promise.all(updates);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to unmark other defaults:', error);
    }
  }
}

/**
 * Map a document snapshot to layout config object
 */
function docToLayoutConfig(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    name: data.name,
    is_public: data.is_public,
    is_default: data.is_default || false,
    author_id: data.author_id,
    layout_data: data.layout_data,
    created_at: data.created_at?.toDate ? data.created_at.toDate() : data.created_at,
    updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : data.updated_at,
  };
}

/**
 * Get layout configurations available to a user
 * Uses two queries (public + own) because Firestore requires queries to only return
 * documents the user has permission to read.
 * Non-admins: public layouts + own private layouts
 * Admins: public + own (same; admin can manage via update/delete rules)
 * @param {string} userId - Current user ID
 * @param {boolean} isAdmin - Whether user is admin (unused; kept for API compatibility)
 * @returns {Promise<Array>} Array of layout configurations
 */
export async function getLayoutConfigs(userId, isAdmin = false) {
  try {
    const collectionRef = collection(db, COLLECTION_NAME);
    const seen = new Set();
    const layouts = [];

    // Query 1: public layouts (user has read permission)
    const publicQuery = query(
      collectionRef,
      where('layout_type', '==', LAYOUT_TYPE),
      where('is_public', '==', true),
      orderBy('created_at', 'desc')
    );
    const publicSnap = await getDocs(publicQuery);
    publicSnap.docs.forEach((docSnap) => {
      layouts.push(docToLayoutConfig(docSnap));
      seen.add(docSnap.id);
    });

    // Query 2: own layouts (user has read permission via author_id)
    if (userId) {
      const ownQuery = query(
        collectionRef,
        where('layout_type', '==', LAYOUT_TYPE),
        where('author_id', '==', userId),
        orderBy('created_at', 'desc')
      );
      const ownSnap = await getDocs(ownQuery);
      ownSnap.docs.forEach((docSnap) => {
        if (!seen.has(docSnap.id)) {
          layouts.push(docToLayoutConfig(docSnap));
          seen.add(docSnap.id);
        }
      });
    }

    // Sort by created_at desc (merge of two already-sorted lists)
    layouts.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });

    return layouts;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to get layout configurations:', error);
    }
    throw error;
  }
}

/**
 * Load a layout configuration by ID
 * @param {string} layoutConfigId - Layout configuration ID
 * @returns {Promise<Object>} Layout data
 */
export async function loadLayoutConfig(layoutConfigId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, layoutConfigId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Layout configuration not found');
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name,
      is_public: data.is_public,
      author_id: data.author_id,
      layout_data: data.layout_data,
      created_at: data.created_at?.toDate ? data.created_at.toDate() : data.created_at,
      updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : data.updated_at,
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to load layout configuration:', error);
    }
    throw error;
  }
}

/**
 * Update a layout configuration
 * @param {string} layoutConfigId - Layout configuration ID
 * @param {Object} updates - Fields to update (name, is_public, layout_data)
 * @returns {Promise<Object>} Updated layout configuration
 */
export async function updateLayoutConfig(layoutConfigId, updates) {
  try {
    const docRef = doc(db, COLLECTION_NAME, layoutConfigId);
    const updateData = {
      ...updates,
      updated_at: serverTimestamp(),
    };
    await updateDoc(docRef, updateData);

    const docSnap = await getDoc(docRef);
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name,
      is_public: data.is_public,
      author_id: data.author_id,
      layout_data: data.layout_data,
      created_at: data.created_at?.toDate ? data.created_at.toDate() : data.created_at,
      updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : data.updated_at,
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to update layout configuration:', error);
    }
    throw error;
  }
}

/**
 * Delete a layout configuration
 * @param {string} layoutConfigId - Layout configuration ID
 * @returns {Promise<void>}
 */
export async function deleteLayoutConfig(layoutConfigId) {
  try {
    const docRef = doc(db, COLLECTION_NAME, layoutConfigId);
    await deleteDoc(docRef);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to delete layout configuration:', error);
    }
    throw error;
  }
}

/**
 * Get current layout state for saving (localStorage + panel states)
 * @returns {Promise<Object>} Current layout state
 */
export async function getCurrentLayoutState() {
  try {
    const layoutState = loadLayoutState();
    return layoutState || {};
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to get current layout state:', error);
    }
    throw error;
  }
}

/**
 * Apply a layout configuration (save to localStorage and optionally set panel states)
 * @param {Object} layoutData - Layout data to apply
 * @param {string|null} layoutConfigId - Layout configuration ID to track
 */
export function applyLayoutConfig(layoutData, layoutConfigId = null) {
  try {
    const { rideViewPanelStates, ...layoutState } = layoutData;

    const stateToSave = {
      ...layoutState,
      ...(layoutConfigId && { currentRideViewLayoutConfigId: layoutConfigId }),
      ...(rideViewPanelStates && { rideViewPanelStates }),
    };
    saveLayoutState(stateToSave);

    if (rideViewPanelStates) {
      localStorage.setItem('rideViewPanelStates', JSON.stringify(rideViewPanelStates));
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to apply layout configuration:', error);
    }
    throw error;
  }
}

/**
 * Get the ID of the currently applied layout configuration
 * @returns {string|null} Current layout config ID or null
 */
export function getCurrentLayoutConfigId() {
  return loadLayoutState()?.currentRideViewLayoutConfigId || null;
}
